import axios from 'axios';
import { ENV, isDev } from '../../../config/env';
import { AUTH_STORAGE_KEYS, HTTP_STATUS } from '../constants';



// Create axios instance with environment configuration
const httpClient = axios.create({
    baseURL: ENV.API_BASE_URL,           // ✅ From environment variables
    timeout: ENV.API_TIMEOUT,           // ✅ From environment variables
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor - Add auth token and logging
httpClient.interceptors.request.use(
    (config) => {
        // Add auth token if available
        const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // If data is FormData, remove Content-Type header to let axios set it with boundary
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        // Development logging
        if (isDev) {
            console.log('🔄 API Request:', {
                method: config.method?.toUpperCase(),
                url: config.url,
                baseURL: config.baseURL,
                data: config.data,
            });
        }

        return config;
    },
    (error) => {
        if (isDev) {
            console.error('❌ Request Error:', error);
        }
        return Promise.reject(error);
    }
);

// Response interceptor - Handle token refresh and error logging
httpClient.interceptors.response.use(
    (response) => {
        // Development logging
        if (isDev) {
            console.log('✅ API Response:', {
                status: response.status,
                url: response.config.url,
                data: response.data,
            });
        }

        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        // Development error logging
        if (isDev) {
            console.error('❌ API Error:', {
                status: error.response?.status,
                url: error.config?.url,
                message: error.response?.data?.detail || error.message,
            });
        }
        
        // Handle 401 - Unauthorized (token refresh)
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && !originalRequest._retry) {
            originalRequest._retry = true;
            
            try {
                const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
                if (refreshToken) {
                    const response = await axios.post(`${ENV.API_BASE_URL}/auth/refresh`, {
                        refresh_token: refreshToken
                    });
                    
                    const { access_token } = response.data;
                    localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, access_token);
                    
                    if (isDev) {
                        console.log('🔄 Token refreshed successfully');
                    }
                    
                    return httpClient(originalRequest);
                }
            } catch (refreshError) {
                if (isDev) {
                    console.error('❌ Token refresh failed:', refreshError);
                }
                
                // Clear storage and redirect to login
                localStorage.clear();
                window.location.href = '/auth/login';
                return Promise.reject(refreshError);
            }
        }
        
        return Promise.reject(error);
    }
);

// Export configured client
export default httpClient;

// Export helper functions
export const isNetworkError = (error: any) => {
    return !error.response && error.request;
};

export const getErrorMessage = (error: any): string => {
    if (isNetworkError(error)) {
        return 'Network error. Please check your connection.';
    }

    // Extract error code and message
    const errorCode = error.response?.data?.error;
    const statusMsg = error.response?.data?.status?.msg;
    const detail = error.response?.data?.detail;
    const genericMsg = error.message;

    // Handle specific error codes for better user experience
    if (errorCode === 'ALREADY_EXISTS' || errorCode === 'DUPLICATE_ERROR') {
        // Return the specific message from backend if available, otherwise provide user-friendly message
        return statusMsg || 'This email has already been registered. Please use a different email or contact support.';
    }

    // Backend error response format: { error: "ERROR_CODE", status: { msg: "Error message", error: true } }
    // Also support FastAPI format: { detail: "Error message" }
    return statusMsg || detail || genericMsg || 'An unexpected error occurred';
};
